// ============================================================
// PIE — PieComposer (presentador: ANALYZE → OPTIMIZE → PRESENT)
// ============================================================
// Orquesta el pipeline completo del PIE sobre el SmartCard Layout
// Engine (FROZEN — se consume, jamás se toca) y SmartMedia (VCE).
//
//   ANALYZE  → useMediaFacts: DOM real → SubjectFacts (fases
//              baseline/measured — P10 Visual Stability)
//   OPTIMIZE → optimizeSync: candidatos (vce-baseline + canon-ratio
//              + real-fit) → invariantes I1-I4 → goals ponderados →
//              argmax → explain (P8) + regressionGuard (P9)
//   PRESENT  → SmartCard FROZEN + SmartMedia con la decisión
//
// MISMA firma de props que SmartComposer (migración sin fricción).
// El motor NO conoce negocios (P1); el tenant configura (P6).
// ============================================================
import React, { useMemo, useRef } from 'react';
import SmartCard from '../SmartCard';
import SmartMedia, { RATIO_CLASS } from '../visual-engine/SmartMedia';
import { normalizeVisualItems } from '../visual-engine/adapters/contentAdapter';
import { optimizeSync } from './optimize';
import { useMediaFacts } from './useMediaFacts';
import './pie.css';
import type { Analyzer, PresentationIntent, SubjectFacts, TenantPieConfig } from './types';

// ── Resolución de ratio del presentador ──
// RATIO_CLASS del VCE (FROZEN) cubre 3/4, 1/1, 16/9. El PIE añade
// '1/2' (canon KF validado por simulación) y '4/3' (futuro). El VCE
// NO se toca: el presentador extiende el mapa hacia afuera (P6).
const PIE_RATIO_CLASS: Record<string, string> = {
  ...RATIO_CLASS,
  '1/2': 'aspect-[1/2]',
  '4/3': 'aspect-[4/3]',
};

export interface PieComposerProps {
  /** Media cruda del consumidor (media[] tipado o images[] legacy) */
  media?: Array<{ type?: 'image' | 'video'; url: string }>;
  images?: string[];
  /** Texto alternativo (a11y — obligatorio en el consumidor) */
  alt?: string;
  blocked?: boolean;
  /** Descriptores NEUTROS para la decisión (los mide el consumidor) */
  infoLength?: number;
  hasDescription?: boolean;
  state?: string;
  actionCount?: number;
  context?: 'card' | 'detail' | 'thumbnail';
  /** Configuración PIE del tenant (único conocimiento de negocio) */
  config?: TenantPieConfig;
  /** Intención de presentación (opcional — token neutro) */
  intent?: PresentationIntent;
  /** Analyzers extra del consumidor (visión futura — registro externo, P6) */
  analyzers?: Record<string, Analyzer>;
  /** Regiones de información (ReactNode ya compuesto por el consumidor) */
  header?: React.ReactNode;
  body?: React.ReactNode;
  footer?: React.ReactNode;
  overlay?: React.ReactNode;
  className?: string;
  /** Evento de UI genérico — el motor propaga, el consumidor decide */
  onClick?: () => void;
}

/** Contenido + contexto → facts base (disponibles SIEMPRE, incluso sin DOM). */
function baseFacts(
  items: ReturnType<typeof normalizeVisualItems>,
  infoLength: number,
  hasDescription: boolean,
  state: string | undefined,
  actionCount: number,
  context: 'card' | 'detail' | 'thumbnail',
): SubjectFacts {
  const types = items.map((i) => (i.type === 'video' ? 'video' : 'image'));
  return {
    mediaCount: items.length,
    mediaTypes: types,
    hasVideo: types.includes('video'),
    hasPoster: items.some((i) => Boolean(i.poster)),
    infoWeight: infoLength > 160 ? 'heavy' : infoLength > 40 ? 'normal' : 'minimal',
    hasDescription,
    state,
    actionCount,
    context,
  };
}

/** Clave estable de memoización (facts + config relevantes). */
function factsKey(facts: Partial<SubjectFacts>): string {
  const pick: Record<string, unknown> = {
    w: facts.naturalWidth,
    h: facts.naturalHeight,
    ra: facts.realAspect,
    lb: facts.hasLetterbox,
    bg: facts.bgUniformity,
    vo: facts.visualOccupancy,
    mc: facts.mediaCount,
    hv: facts.hasVideo,
    iw: facts.infoWeight,
    st: facts.state,
  };
  return JSON.stringify(pick);
}

export const PieComposer: React.FC<PieComposerProps> = ({
  media,
  images,
  alt = '',
  blocked = false,
  infoLength = 0,
  hasDescription = false,
  state,
  actionCount = 0,
  context = 'card',
  config,
  intent,
  header,
  body,
  footer,
  overlay,
  className,
  onClick,
}) => {
  // 1. Normalizar contenido (adaptador — frontera de formatos)
  const items = useMemo(() => normalizeVisualItems(media, images), [media, images]);

  // 2. Contenedor ref para el puente DOM (NO toca SmartMedia FROZEN)
  const containerRef = useRef<HTMLDivElement>(null);

  // 3. ANALYZE — puente DOM → facts (fases baseline/measured, P10)
  const { facts: domFacts, phase } = useMediaFacts(items, containerRef, {
    letterbox: true,
    letterboxBudgetMs: 8,
  });

  // 4. OPTIMIZE — núcleo puro (sella I1, sin cambios)
  const allFacts = useMemo<SubjectFacts>(
    () => ({ ...baseFacts(items, infoLength, hasDescription, state, actionCount, context), ...domFacts }),
    [items, infoLength, hasDescription, state, actionCount, context, domFacts],
  );

  const decision = useMemo(
    () => optimizeSync(allFacts, context, intent ?? {}, config ?? { goals: [] }),
    [allFacts, context, intent, config],
  );

  // 5. PRESENT — SmartCard FROZEN + SmartMedia con la decisión del motor
  const ratio = decision.frame.ratio;
  // 'real' no existe en el mapa → aspect-ratio inline (hallazgo simulación §6.2)
  const ratioClass = PIE_RATIO_CLASS[ratio] ?? undefined;
  const inlineRatio = !ratioClass && ratio === 'real' && allFacts.realAspect
    ? { aspectRatio: `${allFacts.realAspect}` }
    : undefined;

  const capabilities = {
    ...decision.capabilities,
    ...(config?.capabilities ?? {}), // P6: config del tenant gana
  };

  // ── P10 Visual Stability: la transición baseline→measured es SUAVE ──
  // La clase pie-stable aplica transition sobre aspect-ratio/height; la
  // primera pintura (baseline) es idéntica a hoy; el cambio ocurre UNA
  // vez por tarjeta (useMediaFacts garantiza measuredRef).
  const stability = config?.stability ?? {};
  const transitionMs = stability.transitionMs ?? 300;
  const respectReducedMotion = stability.respectReducedMotion ?? true;
  const stabilityClass = phase === 'measured' ? 'pie-stable' : '';
  const stabilityStyle = {
    ...(inlineRatio ?? {}),
    ...(respectReducedMotion
      ? {}
      : { transitionDuration: `${transitionMs}ms` }),
  } as React.CSSProperties;

  // ── infoTreatment (paridad F3): clamp + expansión con labels del tenant ──
  const [expanded, setExpanded] = React.useState(false);
  const clampLines = 3; // hereda kfFeedConfig.infoTreatment.clampLines
  const expandable = true;
  const expandLabel = config?.texts?.expandLabel ?? 'Ver más';
  const collapseLabel = config?.texts?.collapseLabel ?? 'Ver menos';
  const clamped = decision.infoTreatment === 'clamped' && expandable && hasDescription;

  const renderedBody = clamped ? (
    <div>
      <div
        className={expanded ? undefined : 'vce-clamp'}
        style={expanded ? undefined : ({ '--vce-lines': clampLines } as React.CSSProperties)}
      >
        {body}
      </div>
      {hasDescription && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setExpanded(v => !v); }}
          className="mt-1 text-xs font-medium text-purple-600 hover:text-purple-700"
        >
          {expanded ? collapseLabel : expandLabel}
        </button>
      )}
    </div>
  ) : body;

  return (
    <SmartCard
      variant={decision.variant ?? 'default'}
      onClick={onClick}
      className={className}
      regions={{
        media: (
          <div ref={containerRef} className={stabilityClass} style={stabilityStyle}>
            <SmartMedia
              media={media}
              images={images}
              blocked={blocked}
              ratioClass={ratioClass}
              alt={alt}
              capabilities={capabilities}
              config={configToVce(config)}
            />
          </div>
        ),
        overlay,
        header,
        body: renderedBody,
        footer,
      }}
    />
  );
};

/** Traduce TenantPieConfig → TenantVisualConfig (solo campos compatibles). */
function configToVce(config?: TenantPieConfig) {
  if (!config) return undefined;
  return {
    fitPolicy: 'contain' as const,
    mediaRatios: { default: '3/4', heavy: '1/1' },
    capabilities: config.capabilities,
    infoTreatment: { clampLines: 3, expandable: true },
    presentation: {
      mediaBgClass: config.aesthetics?.mediaBgClass ?? 'bg-gray-100',
      interactive: config.aesthetics?.interactive ?? true,
      mediaContainerClass: config.aesthetics?.mediaContainerClass,
    },
    texts: config.texts,
  };
}

export default PieComposer;
